import { landmineNames } from '@mm/constants/landmine-names';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import moment from 'moment';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Input, Label } from 'reactstrap';
import Datetime from '../../../../../common/components/DateTimePicker/DateTimePicker';
import MainTooltip from '../../../../../common/components/MainTooltip/MainTooltip';
import { getTimeZone } from '../../../../../common/helpers/timeZone';
import { INVALID_DATE_MESSAGE } from '../../constants/messages';
import './CalendarBlock.scss';
import RadioInput from '../../../../../common/components/RadioInput';

type Props = {
  selectedDate: moment.Moment;
  setSelectedDate: (selectedDate: moment.Moment) => void;
  useRecipientTimeZone: boolean;
  setUseRecipientTimeZone: (useRecipientTimeZone: boolean) => void;
  allowScheduleTypeChange: boolean;
  sendImmediately: boolean;
  setSendImmediately: (sendImmediately: boolean) => void;
  isSelectedDateValid: boolean;
};

export const DatetimePickerMemoized = React.memo(
  Datetime,
  (l, r) =>
    l.minDate?.toString() === r.minDate?.toString() &&
    l.value.toString() === r.value.toString(),
);

const CalendarBlock = (props: Props) => {
  const {
    selectedDate,
    setSelectedDate,
    useRecipientTimeZone,
    setUseRecipientTimeZone,
    sendImmediately,
    setSendImmediately,
    isSelectedDateValid,
    allowScheduleTypeChange,
  } = props;

  const onDateChange = useCallback(
    date => setSelectedDate(moment(date)),
    [setSelectedDate],
  );

  const modalActions = useModalActions();
  const canScheduleCampaigns = useSelector(
    combinedFeatureFlagsSelector,
  ).campaignsScheduleSends;

  return (
    <div className="campaign-details-calendar">
      <div className="campaign-details-calendar__options">
        <div className="campaign-details-calendar__option">
          <RadioInput
            className="campaign-details-calendar__radio"
            onChange={() => setSendImmediately(true)}
            checked={sendImmediately}
            disabled={!allowScheduleTypeChange}
          >
            Immediately
          </RadioInput>
        </div>
        <div
          className={
            canScheduleCampaigns
              ? 'campaign-details-calendar__option'
              : 'campaign-details-calendar__option--upgrade'
          }
        >
          <RadioInput
            onChange={() => setSendImmediately(false)}
            checked={!sendImmediately}
            disabled={!allowScheduleTypeChange || !canScheduleCampaigns}
            className="campaign-details-calendar__radio"
          >
            Send later
          </RadioInput>
          {!canScheduleCampaigns && (
            <div className="campaign-details-calendar__upgrade-link-container">
              <span
                onClick={() =>
                  modalActions.openUniversalUpgradeLandmineModal({
                    landmineName:
                      landmineNames[
                        'MARKETING-AUTOMATION__CHAT-BLASTER__CALENDAR-BLOCK'
                      ],
                    feature: 'campaignsScheduleSends',
                  })
                }
                className="campaign-details-calendar__upgrade-link-container__link"
              >
                Upgrade your plan!
              </span>
            </div>
          )}
        </div>
      </div>
      {!sendImmediately && !isSelectedDateValid && (
        <MainTooltip
          className="calendar-tooltip"
          innerClassName="calendar-tooltip__calendar-inner-tooltip"
          target="calendar-tooltip"
          warning
          message={INVALID_DATE_MESSAGE}
        />
      )}
      {!sendImmediately && (
        <div className="campaign-details-calendar__date-time-container">
          <DatetimePickerMemoized
            value={selectedDate.toDate()}
            onChange={onDateChange}
            minDate={moment().startOf('day').toDate()}
          />
          <div className="timezone-block">
            <Label className="timezone-block__label">
              <Input
                className="timezone-block__input"
                type="radio"
                onChange={() => setUseRecipientTimeZone(false)}
                checked={!useRecipientTimeZone}
              />
              in my timezone ({getTimeZone()})
            </Label>
            <Label className="timezone-block__label">
              <Input
                className="timezone-block__input"
                type="radio"
                onChange={() => setUseRecipientTimeZone(true)}
                checked={useRecipientTimeZone}
              />
              in each recipient's timezone
            </Label>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarBlock;
